'use client'

import type { AI } from '@/app/action'
import { canAskQuestions } from '@/app/lib/auth-limit'
import { useActions, useAIState, useUIState } from 'ai/rsc'
import { useSession } from 'next-auth/react'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FaArrowUp } from 'react-icons/fa'
import { PiMagnifyingGlassThin } from 'react-icons/pi'
import { useInView } from 'react-intersection-observer'
import Textarea from 'react-textarea-autosize'
import clientComponents from './client-components'
import { FollowupPanel } from './followup-panel'
import AnimText from './ui/anim-text'
import { LoginDialog } from './ui/login-dialog'
import { TextGenerateHeading } from './ui/text-generate-heading'
import { UserMessage } from './user-message'
import { usePostHog } from 'posthog-js/react'

export function ChatPanel({ subdomain }: { subdomain: string }) {
  const session = useSession()
  const posthog = usePostHog()

  let shortPrompts = []
  const isKCBobcat = subdomain === 'kcbobcat'
  if (isKCBobcat) {
    shortPrompts = ['Skid Steers', 'Excavators', 'Parts']
  } else if (subdomain === 'rdo') {
    shortPrompts = [
      'Tractors',
      'Combines',
      'Skid Steers',
      'Excavators',
      'Parts'
    ]
  } else if (subdomain === 'titan') {
    shortPrompts = [
      'Tractors',
      'Combines',
      'Skid Steers',
      'Excavators',
      'Parts'
    ]
  } else if (subdomain === 'ignite') {
    shortPrompts = ['Attachments', 'Parts', 'Implements']
  } else {
    shortPrompts = ['Tractors', 'Combines', 'Skid Steers', 'Excavators']
  }

  const searchParams = useSearchParams()
  const [isOpenLoginDialog, setIsOpenLoginDialog] = useState(false)
  const [input, setInput] = useState('')
  const [messagesUIState, setMessagesUIState] = useUIState<typeof AI>()
  const [aiState, setAIState] = useAIState()
  const { submit } = useActions()
  const [isButtonPressed, setIsButtonPressed] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [showEmptyScreen, setShowEmptyScreen] = useState(false)
  const { ref: questionRef, inView: questionInView } = useInView()
  const outerQuestionRef = useRef<HTMLDivElement | null>(null)
  // This will be used to track which input search param has been processed
  const processedInputSearchParam = useRef<string | null>(null)

  const { title } = clientComponents[subdomain] || clientComponents.default

  // Focus on input when button is pressed
  useEffect(() => {
    if (isButtonPressed) {
      inputRef.current?.focus()
      setIsButtonPressed(false)
    }
  }, [isButtonPressed])

  const setRefs = useCallback(
    (node: HTMLDivElement) => {
      // Ref's from useRef needs to have the node assigned to `current`
      outerQuestionRef.current = node
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      questionRef(node)
    },
    [questionRef]
  )

  const askQuestion = useCallback(
    async (formData: FormData) => {
      // Submit and get response message
      const canAsk = await canAskQuestions({
        sessionStatus: session.status,
        subdomain
      })

      if (!canAsk) {
        setIsOpenLoginDialog(true)
        return
      }

      const userQuery = formData.get('input') as string

      // Track the user query with PostHog
      posthog?.capture('user_query', {
        query: userQuery,
        subdomain,
        userId: session?.data?.user?.id || 'anonymous'
      })

      // Add user message to UI state
      setMessagesUIState(currentMessages => [
        ...currentMessages,
        {
          id: Date.now(),
          component: (
            <div ref={setRefs}>
              <UserMessage message={userQuery} />
            </div>
          )
        }
      ])

      const responseMessage = await submit(formData)
      setMessagesUIState(currentMessages => [
        ...currentMessages,
        responseMessage as any
      ])

      setInput('')
    },
    [
      session.status,
      subdomain,
      setMessagesUIState,
      submit,
      setRefs,
      posthog,
      session?.data?.user?.id
    ]
  )

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Clear messages if button is pressed
    if (isButtonPressed) {
      handleClear()
      setIsButtonPressed(false)
    }

    const formData = new FormData(e.currentTarget)
    await askQuestion(formData)
  }

  useEffect(() => {
    const input = searchParams.get('input')
    if (input && processedInputSearchParam.current !== input) {
      // Mark the input search param as processed
      processedInputSearchParam.current = input
      setInput(input)
      const formData = new FormData()
      formData.append('input', input)
      askQuestion(formData)
    }
  }, [searchParams, askQuestion])

  // console.log('aiState', aiState)

  // Clear messages
  const handleClear = () => {
    setIsButtonPressed(true)
    setMessagesUIState([])
    setAIState({ messages: [], chatId: '' })
  }

  useEffect(() => {
    // focus on input when the page loads
    inputRef.current?.focus()
  }, [])

  // If there are messages and the new button has not been pressed, display the new Button
  if (messagesUIState.length > 0 && !isButtonPressed) {
    return (
      <>
        <div className="sticky bottom-0 bg-white px-4 pb-12 lg:px-0">
          <FollowupPanel />
          {/* <div className="fixed bottom-2 bg-white md:bottom-8 left-0 right-0 flex justify-center items-center mx-auto">
          <Button
            type="button"
            variant={'secondary'}
            className="rounded-full bg-secondary/80 group transition-all hover:scale-105"
            onClick={() => handleClear()}
          >
            <span className="text-sm mr-2 group-hover:block hidden animate-in fade-in duration-300">
              New
            </span>
            <Plus size={18} className="group-hover:rotate-90 transition-all" />
          </Button>
        </div> */}
        </div>
        {!questionInView && (
          <button
            onClick={() => {
              outerQuestionRef.current?.scrollIntoView({ behavior: 'smooth' })
            }}
            className="fixed right-4 top-28 rounded-full border border-gray-200 bg-white p-3 text-lg text-black lg:right-72"
          >
            <FaArrowUp />
          </button>
        )}
      </>
    )
  }

  return (
    <>
      <LoginDialog
        open={isOpenLoginDialog}
        handleClose={() => setIsOpenLoginDialog(false)}
      />
      <div className="flex flex-1 flex-col items-center justify-center gap-4 pb-36">
        {/* <IconKuroko className="w-6 h-6 mb-4" /> */}
        <TextGenerateHeading
          className="mb-4 text-4xl font-bold sm:text-5xl"
          text={title}
        />
        <AnimText delay={1} />
        <form
          onSubmit={handleSubmit}
          className="flex w-full max-w-2xl flex-col gap-5 px-6"
        >
          <div className="relative flex w-full items-center">
            <Textarea
              ref={inputRef}
              name="input"
              rows={1}
              tabIndex={0}
              placeholder="What can I help you find?"
              spellCheck={false}
              value={input}
              className="hover flex max-h-60 w-full resize-none rounded-full border border-input py-3 pl-8 pr-14 text-base leading-5 ring-offset-background placeholder:text-muted-foreground hover:shadow-lg focus-visible:shadow-lg focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 sm:pl-10"
              onChange={e => {
                setInput(e.target.value)
                setShowEmptyScreen(e.target.value.length === 0)
              }}
              onKeyDown={e => {
                // Enter should submit the form
                if (e.key === 'Enter' && !e.shiftKey) {
                  // Prevent the default action to avoid adding a new line
                  e.preventDefault()
                  const textarea = e.target as HTMLTextAreaElement
                  textarea.form?.requestSubmit()
                }
              }}
              onHeightChange={height => {
                // Ensure inputRef.current is defined
                if (!inputRef.current) return

                // The initial height and left padding is 70px and 2rem
                const initialHeight = 48
                // The initial border radius is 32px
                const initialBorder = 24
                // The height is incremented by multiples of 20px
                const multiple = (height - initialHeight) / 17

                // Decrease the border radius by 4px for each 20px height increase
                const newBorder = initialBorder - 4 * multiple
                // The lowest border radius will be 8px
                inputRef.current.style.borderRadius =
                  Math.max(8, newBorder) + 'px'
              }}
              onFocus={() => setShowEmptyScreen(true)}
              onBlur={() => setShowEmptyScreen(false)}
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 transform text-xl">
              <PiMagnifyingGlassThin />
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            {shortPrompts.map(prompt => (
              <button
                key={prompt}
                className="border-light whitespace-nowrap rounded-full border px-3 py-2 text-start text-sm shadow-sm transition enabled:hover:bg-slate-50"
                onClick={() => setInput('Show Me ' + prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
          {/* <EmptyScreen
          submitMessage={message => {
            setInput(message)
          }}
          className={cn(showEmptyScreen ? 'visible' : 'invisible')}
        /> */}
        </form>
      </div>
    </>
  )
}
